
import KeyboardArrowDownOutlinedIcon from '@mui/icons-material/KeyboardArrowDownOutlined';
import { Button, Tooltip } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { clone, each } from 'lodash';
import { useEffect, useRef, useState } from 'react';

import { useAtom } from 'jotai';
import {
    CANVAS_TYPE,
    CATEGORIES_BY_LEGACY_SORT_KEY,
    CHANNELS,
    CHANNELS_ABBREVIATION,
    COST_STRUCTURE,
    CUSTOMER_RELATIONSHIPS,
    CUSTOMER_RELATIONSHIPS_ABBREVIATION,
    CUSTOMER_SEGMENTS,
    DISTRIBUTION_CHANNELS,
    DISTRIBUTION_CHANNELS_ABBREVIATION,
    EARLY_ADOPTERS,
    EXISTING_ALTERNATIVES,
    EXISTING_ALTERNATIVES_ABBREVIATION,
    HIGH_LEVEL_CONCEPT,
    HIGH_LEVEL_CONCEPT_ABBREVIATION,
    KEY_ACTIVITIES,
    KEY_ACTIVITIES_ABBREVIATION,
    KEY_METRICS,
    KEY_METRICS_ABBREVIATION,
    KEY_PARTNERS,
    KEY_PARTNERS_ABBREVIATION,
    KEY_RESOURCES,
    KEY_RESOURCES_ABBREVIATION,
    PARTNERS,
    PARTNERS_ABBREVIATION,
    PROBLEM,
    PROBLEM_ABBREVIATION,
    REVENUE_STREAMS,
    REVENUE_STREAMS_ABBREVIATION,
    REVENUE_STRUCTURES,
    REVENUE_STRUCTURES_ABBREVIATION,
    SOLUTION,
    SOLUTION_ABBREVIATION,
    UNFAIR_ADVANTAGE,
    UNFAIR_ADVANTAGE_ABBREVIATION,
    UNIQUE_VALUE_PROPOSITIONS,
    UNIQUE_VALUE_PROPOSITIONS_ABBREVIATION,
    VALUE_PROPOSITIONS,
    VALUE_PROPOSITIONS_ABBREVIATION
} from "../../../constants";
import { canvasAtom } from '../../../stores/jotai/canvas/canvas';

interface IProps {
    currentCategory: string;
    handleCategoryClick: Function;
    categories: { [key: string]: CanvasCategory };

    handleOpen?: Function;
    handleClose?: Function;

    baseAtomBridge: any;

    renderPlaceholder: boolean;

    team: TeamState;
    appState: AppState;
    disabled?: boolean;

    // Is our category in "sub" mode
    // and should we handle that
    // when displaying an abbreviation
    abbreviationIsSub?: boolean;
    canvasData?:any;
}

const ROOT_SELECTOR_STYLE = {
    display: 'inline-block', 
    position:'relative',
    width:'100%',
    minWidth: '210px', 
    maxWidth: '210px',
    height: '100px', 
    zIndex: '2000',
    marginLeft: '0px',
    boxShadow:'0px 4px 6px -1px rgba(0, 0, 0, 0.20)',
    borderRadius:'3px',
};

// Not ideal but it get's us a demo-facing work in progress to show off
const COHORT_BMC_RELABEL_MAP = {
    titles: {
        'Key Partners': 'Problem',
        'Key Activities': 'Solution',
        'Key Resources': 'Key Metrics',
        'Customer Relationships': 'Unfair Advantage'
    },
    descriptions: {
        'What Key Activities do our Value Propositions require?': 'Outline a possible solution for each problem',
        'Who are our Key Partners?': 'List your customer\'s top 3 problems',
        'What Key Resources do our Value Propositions require?': 'List the key numbers that tell you how your business is doing',
        'What type of relationships do our Customer Segments expect?': 'Something that can not be easily copied or bought'
    },
    sub: {
        'Key Partners': 'Existing Alternatives',
        'Value Propositions': 'High-Level Concept',
        'Customer Segments': 'Early Adopters'
    }
}

export const BmcSelector = ({
    currentCategory,
    handleCategoryClick,
    categories,
    handleOpen,
    handleClose,
    baseAtomBridge,
    renderPlaceholder,
    team,
    appState,
    disabled,
    abbreviationIsSub,
    canvasData,
}: IProps)=>{

    const [visible, setVisible] = useState<boolean>(false);
    const rootNode = useRef<HTMLDivElement>(null);;
    const [canvasState, setCanvasState] = useAtom(canvasAtom);
    const theme = useTheme();
    useEffect(() => {
        document.addEventListener("keyup", handleEscapeCheck);
        document.addEventListener("mousedown", handleEscapeCheck);
        return () => {
          document.removeEventListener("keyup", handleEscapeCheck);
          document.removeEventListener("mousedown", handleEscapeCheck);
        };
    }, [visible]);

    const handleEscapeCheck = (event) => {
        if ((event.key && event.key === "Escape") || (event.target && !rootNode?.current?.contains(event.target))) {
            handleProcessEscaping();
        }
    }

    const handleProcessEscaping = () => {
        if (visible) {
            setVisible(false);
        }
        if (handleClose) {
            handleClose();
        }
    }

    const handleOpeningBMC = () => {
        setVisible(prevVisible => !prevVisible);

        if (handleOpen) {
            handleOpen();
        }
    }

   

    const getIsUsingBMCVersion = () => {
        const canvasType = canvasData?.type;
        const validCanvasTypes = ['LeanCanvas', 'BusinessModelCanvas', 'CustomCanvas', 'DThreeCanvas'];
        return validCanvasTypes.includes(canvasType) ? canvasType : 'BusinessModelCanvas';
    }

    const getCurrentCategory = ():CanvasCategory => {
        let foundCategory = undefined;
        const canvas = canvasData?.type;
        const canvasAbbreviation = CANVAS_TYPE[canvas] || CANVAS_TYPE.BusinessModelCanvas;

        each(categories, category => {
            if (category?.categoryID === currentCategory) {    
                foundCategory = getCategoryByNameOrAbbr({
                    canvasType: canvasAbbreviation,
                    name: category?.name,
                });
            }
        });
        
        return foundCategory;
    }

    const mapGivenAbbr = (givenArrb:string) => {
        if (team && team.canvasType === "MissionModelCanvas") {
            if (givenArrb === 'RS') return 'MA';
            if (givenArrb === 'CS') return 'MC';
            if (givenArrb === 'CH') return 'DP';
            if (givenArrb === 'CR') return 'BS';

            return givenArrb;
        }
        else {
            return givenArrb;
        }
    }

    const mapGivenName = (name: string): string => {
        if (
            team &&
            team.canvasType === "MissionModelCanvas"
        ) {
            if (name === "Customer Segments") {
                return "Beneficiaries";
            }

            if (name === "Cost Structure") {
                return "Mission Cost";
            }
        }

        return name;
    }


    const getCategoryByNameOrAbbr = ({
        canvasType,
        name,
        abbreviation,
    }: {
        canvasType?: string;
        name?: string;
        abbreviation?: string;
    }): CanvasCategory => {
        const abbreviationToCheck = abbreviation
            ? mapGivenAbbr(abbreviation)
            : abbreviation;
            
        const nameToCheck = name ? mapGivenName(name) : name;
        const categoryValues = Object.values(categories || {});
        let foundCategory = null;

        for (let index = 0; index < categoryValues?.length; index++) {
            const category = categoryValues[index];
            const categoriesByLegacySortKey: Record<
                string,
                { name: string; abbreviation: string }
            > = CATEGORIES_BY_LEGACY_SORT_KEY[canvasType];
            const validCategoryEntry = categoriesByLegacySortKey
                ? Object.entries(categoriesByLegacySortKey).find(
                      ([sortKey, category]) =>
                          category.abbreviation === abbreviationToCheck ||
                          category.name === nameToCheck
                  )
                : [];
            const legacySortKey = validCategoryEntry?.length>0 ? parseInt(validCategoryEntry[0]) : '';
            const categoryInfoFromLocal = validCategoryEntry?.length>0 ? validCategoryEntry[1]: '';

            if (
                category.legacySortKey === legacySortKey ||
                (!legacySortKey &&
                    category.abbreviation === abbreviationToCheck) ||
                (!legacySortKey && category.name === nameToCheck)
            ) {
                foundCategory = {
                    ...category,
                    name:
                        category?.name !== "na"
                            ? category?.name
                            : categoryInfoFromLocal?.name,
                    abbreviation:
                        category?.abbreviation ||
                        categoryInfoFromLocal?.abbreviation,
                };
            }
        }

        return foundCategory;
    };

    const getCurrentCanvas = () => {
        let buffer = [];
        for (let i = 0; i < canvasState?.library?.length; i++) {
            if (canvasState?.library[i]?.name === canvasData?.type) {
                buffer = canvasState?.library[i]?.layout?.layout;
            }
        }
        
        return buffer;
    }

    const renderInnerCategoryLabel = (categoryID: string, categoryName: string, categoryAbbreviation: string, isASubCategory: boolean) => {

        const CAT_LABEL_STYLE = {
            display: 'flex',
            width: '100%',
            height: '100%',
            boxSizing: 'content-box',
            cursor: 'pointer',
            margin: 'auto',
            textAlign: 'center',
            fontWeight: '600',
            fontSize: '13px',
            justifyContent: 'center',
            alignItems: 'center',
            color: theme.custom.mainHeaderColor,
        };
        
        let CAT_LABEL_CLASS = "discovery-hypothesis-link-canvas-label";

        if (currentCategory === categoryID) {
            CAT_LABEL_CLASS += " discovery-hypothesis-link-canvas-label-active";
        }

        let tooltipValue = categoryName;
        let labelValue = categoryAbbreviation;

        if (appState.canvas.orientation === 'lean') {
            // Pass the tooltip and the abbreviation through our mask
            if (COHORT_BMC_RELABEL_MAP.titles[tooltipValue]) {
                tooltipValue = COHORT_BMC_RELABEL_MAP.titles[tooltipValue];
            }

            // Do the abbreviations manually
            if (labelValue === 'KP') labelValue = 'PR';
            if (labelValue === 'KA') labelValue = 'SO';
            if (labelValue === 'CR') labelValue = 'UA';
        }

        return <span onClick={() => {
            handleProcessEscaping();
            handleCategoryClick(categoryID);
        }}>
            <span style={CAT_LABEL_STYLE} className={CAT_LABEL_CLASS}>
                {labelValue}
            </span>
        </span>
    }

    const renderCategoryLabel = (category: CanvasCategory) => {
        if (!category) return <span />;

        return renderInnerCategoryLabel(
            category?.categoryID,
            category?.name,
            category?.abbreviation,
            false
        );
    };


    const renderCanvasSelector = (canvasType) => {
        switch (canvasType) {
            case 'BusinessModelCanvas':
                return renderBMCSelector();
            case 'LeanCanvas':
                return renderLeanSelector();
            case 'CustomCanvas':
                return renderCustomSelector();
            case  'DThreeCanvas':
                return renderDThreeSelector();
            default:
                return renderBMCSelector();
        }
    }


    const renderCategories = (
      categories,
      categoryOrder,
      currentCanvasLayout
    ) => {
      return (
        <span style={ROOT_SELECTOR_STYLE}>
          {currentCanvasLayout?.length ? (
            categoryOrder.map((categoryKey, categoryIndex) => {
              const category = categories[categoryKey];

              // Find the first layout that matches the category's name
              const matchedLayout = currentCanvasLayout?.find(
                (layout) => Number(layout?.i) === category?.legacySortKey
              );
              // If a layout is found, render it
              if (matchedLayout) {
                return (
                  <Tooltip
                    title={
                        canvasData?.type === "DThreeCanvas" &&
                      category?.name === "Customer Segments"
                        ? "Customer Segments and Stakeholders"
                        : category?.name
                    }
                    key={`category-${categoryIndex}`}
                  >
                    <span
                      key={`layout-${categoryIndex}`}
                      style={{
                        border: `1px solid ${theme.custom.borderGrey}`,
                        position: "absolute",
                        width: `${matchedLayout.w}%`,
                        height: `${matchedLayout.h}%`,
                        top: `${matchedLayout.y}%`,
                        left: `${matchedLayout.x}%`
                      }}
                    >
                      {renderCategoryLabel(category)}
                    </span>
                  </Tooltip>
                );
              }

              return null;
            })
          ) : (
            <div
              style={{
                position: "absolute",
                top: `30%`,
                left: `20%`
              }}
            >
              Canvas isn't available
            </div>
          )}
          <div className="clearfix" />
        </span>
      );
    };
      
      const renderSelector = (canvasType, categoriesConfig, categoryOrder) => {
        const categories = {};
        categoryOrder.forEach((categoryKey) => {
          categories[categoryKey] = getCategoryByNameOrAbbr({
            canvasType,
            name: categoriesConfig[categoryKey].name,
            abbreviation: categoriesConfig[categoryKey].abbreviation,
          });
        });
      
        const currentCanvasLayout = getCurrentCanvas();
        return renderCategories(categories, categoryOrder, currentCanvasLayout);
      };

      const renderBMCSelector = () => {
        const categoriesConfig = {
          [KEY_PARTNERS]: { abbreviation: KEY_PARTNERS_ABBREVIATION },
          [KEY_ACTIVITIES]: { abbreviation: KEY_ACTIVITIES_ABBREVIATION },
          [KEY_RESOURCES]: { abbreviation: KEY_RESOURCES_ABBREVIATION },
          [VALUE_PROPOSITIONS]: { abbreviation: VALUE_PROPOSITIONS_ABBREVIATION },
          [CUSTOMER_RELATIONSHIPS]: { abbreviation: CUSTOMER_RELATIONSHIPS_ABBREVIATION, },
          [CHANNELS]: { abbreviation: CHANNELS_ABBREVIATION },
          [CUSTOMER_SEGMENTS]: { name: CUSTOMER_SEGMENTS },
          [COST_STRUCTURE]: { name: COST_STRUCTURE },
          [REVENUE_STREAMS]: { abbreviation: REVENUE_STREAMS_ABBREVIATION },
        };
      
        const categoryOrder = Object.keys(categoriesConfig);
        return renderSelector("BMC", categoriesConfig, categoryOrder);
      };
      
      const renderCustomSelector = () => {
        const categoriesConfig = {
          [KEY_PARTNERS]: { abbreviation: KEY_PARTNERS_ABBREVIATION },
          [KEY_ACTIVITIES]: { abbreviation: KEY_ACTIVITIES_ABBREVIATION },
          [VALUE_PROPOSITIONS]: { abbreviation: VALUE_PROPOSITIONS_ABBREVIATION },
          [CUSTOMER_RELATIONSHIPS]: {
            abbreviation: CUSTOMER_RELATIONSHIPS_ABBREVIATION,
          },
          [CUSTOMER_SEGMENTS]: { name: CUSTOMER_SEGMENTS },
          [COST_STRUCTURE]: { name: COST_STRUCTURE },
        };
      
        const categoryOrder = Object.keys(categoriesConfig);
        return renderSelector("CUSTOM", categoriesConfig, categoryOrder);
      };

      const renderDThreeSelector = () => {
        const categoriesConfig = {
            [PARTNERS]: { abbreviation: PARTNERS_ABBREVIATION },
            [UNIQUE_VALUE_PROPOSITIONS]: { abbreviation: UNIQUE_VALUE_PROPOSITIONS_ABBREVIATION }, 
            [CUSTOMER_SEGMENTS]: { name: CUSTOMER_SEGMENTS }, 
            [PROBLEM]: { abbreviation: PROBLEM_ABBREVIATION },
            [SOLUTION]: { abbreviation: SOLUTION_ABBREVIATION }, 
            [UNFAIR_ADVANTAGE]: { abbreviation: UNFAIR_ADVANTAGE_ABBREVIATION }, 
            [DISTRIBUTION_CHANNELS]: { abbreviation: DISTRIBUTION_CHANNELS_ABBREVIATION },
            [COST_STRUCTURE]: { name: COST_STRUCTURE }, 
            [REVENUE_STRUCTURES]: { abbreviation: REVENUE_STRUCTURES_ABBREVIATION }, 
            [EXISTING_ALTERNATIVES]: { abbreviation: EXISTING_ALTERNATIVES_ABBREVIATION }, 
        };
      
        const categoryOrder = Object.keys(categoriesConfig);
        return renderSelector("DTHREE", categoriesConfig, categoryOrder);
      };
      
      const renderLeanSelector = () => {
        const categoriesConfig = {
          [PROBLEM]: { abbreviation: PROBLEM_ABBREVIATION },
          [SOLUTION]: { abbreviation: SOLUTION_ABBREVIATION },
          [KEY_METRICS]: { abbreviation: KEY_METRICS_ABBREVIATION },
          [VALUE_PROPOSITIONS]: { abbreviation: VALUE_PROPOSITIONS_ABBREVIATION },
          [UNFAIR_ADVANTAGE]: { abbreviation: UNFAIR_ADVANTAGE_ABBREVIATION },
          [CHANNELS]: { abbreviation: CHANNELS_ABBREVIATION },
          [CUSTOMER_SEGMENTS]: { name: CUSTOMER_SEGMENTS },
          [COST_STRUCTURE]: { name: COST_STRUCTURE },
          [REVENUE_STREAMS]: { abbreviation: REVENUE_STREAMS_ABBREVIATION },
          [EXISTING_ALTERNATIVES]: { name: EXISTING_ALTERNATIVES },
          [EARLY_ADOPTERS]: { name: EARLY_ADOPTERS },
          [HIGH_LEVEL_CONCEPT]: { abbreviation: HIGH_LEVEL_CONCEPT_ABBREVIATION },
        };
      
        const categoryOrder = Object.keys(categoriesConfig);
        return renderSelector("LEAN", categoriesConfig, categoryOrder);
      };

    const getToggleCategory = () => {
        let ourCategory = clone(getCurrentCategory());

        // Map all of our lean canvas "sub" categories
        if (abbreviationIsSub === true) {
            if (ourCategory.abbreviation === "PM") {
                ourCategory.abbreviation = "EA";
                ourCategory.name = "Existing Alternatives";
            }

            if (ourCategory.abbreviation === "VP") {
                ourCategory.abbreviation = "HC";
                ourCategory.name = "High-Level Concept";
            }

            if (ourCategory.name === "Customer Segments") {
                ourCategory.abbreviation = "EA";
                ourCategory.name = "Early Adopters";
            }
        }

        return ourCategory;
    }

    const renderToggleButton = () => {
        const ourCategory = getCurrentCategory();

        const toggleCategory = getToggleCategory();

        if (ourCategory === undefined) {
            return <div style={{  }} >
                <Button sx={{ padding: '5px 10px', '.MuiButton-startIcon': { marginLeft: '0px' } }} disabled={disabled} endIcon={<KeyboardArrowDownOutlinedIcon />} variant='outlined' onClick={handleOpeningBMC}>
                    Link Insight to hypothesis
                </Button>
            </div>
        }
        else {
            return <Tooltip title={canvasData?.type==="DThreeCanvas" && toggleCategory?.name==="Customer Segments" ? "Customer Segments and Stakeholders" : toggleCategory?.name} placement="top">
                <Button
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          maxWidth:'100%',
          padding: '5px 8px 5px 9px',
          '.MuiButton-startIcon': { marginLeft: '0px' }
        }}
        onClick={()=>handleOpeningBMC()}
        endIcon={<KeyboardArrowDownOutlinedIcon />}
        variant="outlined"
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {canvasData?.type==="DThreeCanvas" && toggleCategory?.name==="Customer Segments" ? "Customer Segments and Stakeholders" : toggleCategory?.name}
        </span>
      </Button>
            </Tooltip>
        }
    }

    let innards = renderToggleButton();
    let innardsBox= <></>;

    if (visible) {
        innardsBox = renderCanvasSelector(getIsUsingBMCVersion());
    }
    
    if (visible && renderPlaceholder) { innardsBox = renderCanvasSelector(getIsUsingBMCVersion()); }
    return(
        <div style={{ display: 'block',position:'relative' }} ref={rootNode}>
        {innards}
        <div style={{background:"#FFFFFF",position:'absolute',zIndex:11}}>
           {innardsBox}
        </div>
       </div>
    )
}